import { useEffect, useState } from "react";
import Card from "react-bootstrap/Card";
import { ChevronDown, ChevronUp, X } from "react-feather";
import commaFormat from "@utils/commaFormat";
import IncludeExcludeFilter from "./includeExcludeFilter.jsx";
import { useDebounce } from "@utils/utils";
import LoaderImg from "@assets/images/loader.gif";
import { useSelector } from "react-redux";
import ViewAllFilter from "./viewAll-filter.jsx";
import { toast } from "react-toastify";
import { LinearDeterminateLoading } from "./LinearDeterminateLoading.jsx";
import nodataimage from "@assets/images/common/no-data.png";

const HsCodeViewAll = ({ label, emptyContent, onApply, closeFilter, filterList, filterKeyword, includeFilter, appliedFilterList, excludeFilter, filterDetails, getFilterDataHandler }) => {
  const shipmentDetails = useSelector((state) => state.shipmentDetails);
  const [includeFilterButton, setIncludeFilterButton] = useState("true");
  const filterLoader = useSelector((state) => state.shipmentDetails.filterLoader);
  const [selectedList, setSelectedList] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [searchedValue, setSearchedValue] = useState("");
  const [expandedKey, setExpandedKey] = useState("");
  const [expandedSubKey, setExpandedSubKey] = useState("");
  const debouncedInputValue = useDebounce(searchedValue, 500);
  const [isLoaderActive, setIsLoaderActive] = useState(false);

  useEffect(() => {
    if (includeFilter.includes(filterKeyword)) {
      setIncludeFilterButton("true");
    } else if (excludeFilter.includes(filterKeyword)) {
      setIncludeFilterButton("false");
    }
  }, []);

  useEffect(() => {
    setInputValue(debouncedInputValue);
    setIsLoaderActive(false);
    setExpandedKey("");
    setExpandedSubKey("");
  }, [debouncedInputValue]);

  const expandParentSelections = (selectedKeys) => {
    const expanded = { ...selectedKeys };

    const allAvailableCodes = getAllAvailableHSCodes();

    // For each 2-digit code, check if all its 4-digit children are selected
    allAvailableCodes.twoDigitCodes.forEach((twoDigitCode) => {
      const fourDigitChildren = allAvailableCodes.fourDigitCodes.filter((code) => code.startsWith(twoDigitCode) && code.length === 4);

      if (fourDigitChildren.length > 0 && fourDigitChildren.every((child) => selectedKeys[child])) {
        expanded[twoDigitCode] = true;
      }

      // For each 4-digit code, check if all its 6-digit children are selected
      fourDigitChildren.forEach((fourDigitCode) => {
        const sixDigitChildren = allAvailableCodes.sixDigitCodes.filter((code) => code.startsWith(fourDigitCode) && code.length === 6);

        if (sixDigitChildren.length > 0 && sixDigitChildren.every((child) => selectedKeys[child])) {
          expanded[fourDigitCode] = true;
        }
      });
    });

    return expanded;
  };

  // Helper function to get all available HS codes from current data
  const getAllAvailableHSCodes = () => {
    const result = {
      twoDigitCodes: [],
      fourDigitCodes: [],
      sixDigitCodes: [],
    };

    if (filterList && Array.isArray(filterList)) {
      filterList.forEach((twoDigitItem) => {
        if (twoDigitItem.key && twoDigitItem.key.length === 2) {
          result.twoDigitCodes.push(twoDigitItem.key);

          // Get cached 4-digit codes
          const fourDigitChildren = getNestedData(twoDigitItem.key) || [];
          fourDigitChildren.forEach((fourDigitItem) => {
            if (fourDigitItem.key && fourDigitItem.key.length === 4) {
              result.fourDigitCodes.push(fourDigitItem.key);

              // Get cached 6-digit codes
              const sixDigitChildren = getNestedData(twoDigitItem.key, fourDigitItem.key) || [];
              sixDigitChildren.forEach((sixDigitItem) => {
                if (sixDigitItem.key && sixDigitItem.key.length === 6) {
                  result.sixDigitCodes.push(sixDigitItem.key);
                }
              });
            }
          });
        }
      });
    }

    // Also check the Redux store for any additional cached data
    if (shipmentDetails.filterObject && shipmentDetails.filterObject[filterDetails.key]) {
      const cachedData = shipmentDetails.filterObject[filterDetails.key];
      Object.values(cachedData).forEach((twoDigitItem) => {
        if (twoDigitItem.key && twoDigitItem.key.length === 2) {
          if (!result.twoDigitCodes.includes(twoDigitItem.key)) {
            result.twoDigitCodes.push(twoDigitItem.key);
          }

          if (twoDigitItem.values && Array.isArray(twoDigitItem.values)) {
            twoDigitItem.values.forEach((fourDigitItem) => {
              if (fourDigitItem.key && fourDigitItem.key.length === 4) {
                if (!result.fourDigitCodes.includes(fourDigitItem.key)) {
                  result.fourDigitCodes.push(fourDigitItem.key);
                }

                if (fourDigitItem.values && Array.isArray(fourDigitItem.values)) {
                  fourDigitItem.values.forEach((sixDigitItem) => {
                    if (sixDigitItem.key && sixDigitItem.key.length === 6) {
                      if (!result.sixDigitCodes.includes(sixDigitItem.key)) {
                        result.sixDigitCodes.push(sixDigitItem.key);
                      }
                    }
                  });
                }
              }
            });
          }
        }
      });
    }

    return result;
  };

  useEffect(() => {
    let temp = {};

    // First, mark all applied filters as selected
    appliedFilterList.forEach((element) => {
      temp[element] = true;
    });

    // Then, determine which parent codes should be visually selected
    // based on their children being selected
    const expandedTemp = expandParentSelections(temp);

    setSelectedList(expandedTemp);
  }, [appliedFilterList]);

  const changeIncludeExcludeSelection = (data) => {
    if (includeFilterButton !== data) {
      setIncludeFilterButton(data);
    }
  };

  const getAllDescendantKeys = (node) => {
    let keys = [];
    if (node.values && Array.isArray(node.values)) {
      node.values.forEach((child) => {
        keys.push(child.key);
        keys = keys.concat(getAllDescendantKeys(child));
      });
    }
    return keys;
  };

  // Utility: Check if all children are selected
  const areAllChildrenSelected = (children, selectedSet) => {
    if (!children || children.length === 0) return false;
    return children.every((child) => selectedSet[child.key]);
  };

  // Update handle_Select to support hierarchical selection
  const handleSelect = (isChecked, keyy, dataKey, children = [], debouncedInputValue) => {
    let key = Array.isArray(keyy) && keyy.length > 0 ? keyy.filter((item) => item.key.startsWith(debouncedInputValue)) : keyy;

    let temp = { ...selectedList };
    if (isChecked) {
      if (Array.isArray(key) && key.length) {
        // Selecting a parent: select all children
        key.forEach((item) => {
          temp[item.key] = true;
          if (item.values && Array.isArray(item.values)) {
            getAllDescendantKeys(item).forEach((descKey) => {
              temp[descKey] = true;
            });
          }
        });
        if (dataKey) temp[dataKey] = true;
      } else {
        temp[key] = true;
        // Select parent if all siblings are selected
        if (dataKey && areAllChildrenSelected(children, { ...temp, [key]: true })) {
          temp[dataKey] = true;
          // 🔁 New Logic: Select grandparent if all parent's siblings are selected
          const grandParentKey = dataKey.slice(0, -2); // assuming hierarchical keys
          if (grandParentKey) {
            // Get all parent's siblings
            const parentSiblings = children?.filter((item) => item?.key?.startsWith(grandParentKey) && item?.key?.length === dataKey.length);

            const allParentSiblingsSelected = parentSiblings?.length > 0 && parentSiblings.every((sibling) => temp[sibling.key]);
            if (allParentSiblingsSelected) {
              temp[grandParentKey] = true;
            }
          }
        }
      }
    } else {
      if (Array.isArray(key) && key.length) {
        key.forEach((item) => {
          delete temp[item.key];
          getAllDescendantKeys(item).forEach((descKey) => {
            delete temp[descKey];
          });
        });
        let twoDigitParent = dataKey.slice(0, 2);
        if (temp[twoDigitParent]) delete temp[twoDigitParent];
        if (temp[dataKey]) delete temp[dataKey];
      } else {
        delete temp[key];
        let twoDigitParent = key.slice(0, 2);
        let fourDigitParent = key.slice(0, 4);
        if (temp[twoDigitParent]) delete temp[twoDigitParent];
        if (temp[fourDigitParent]) delete temp[fourDigitParent];

        if (key.length !== 6 && children.length > 0) {
          children.forEach((child) => {
            delete temp[child.key];
            getAllDescendantKeys(child).forEach((descKey) => {
              delete temp[descKey];
            });
          });
        }
      }
    }
    setSelectedList({ ...temp });
  };

  const handleRemove = (hsCode) => {
    let selectedListCopy = JSON.parse(JSON.stringify(selectedList));
    if (selectedListCopy[hsCode]) {
      delete selectedListCopy[hsCode];

      Object.keys(selectedListCopy).forEach((item) => {
        if (item.startsWith(hsCode)) {
          delete selectedListCopy[item];
        }
      });
    }
    if (selectedListCopy[hsCode.slice(0, 2)]) {
      delete selectedListCopy[hsCode.slice(0, 2)];
    }
    if (selectedListCopy[hsCode.slice(0, 4)]) {
      delete selectedListCopy[hsCode.slice(0, 4)];
    }
    setSelectedList(JSON.parse(JSON.stringify(selectedListCopy)));
  };

  const checkIfParentSelected = (data) => {
    if (!data.values) return false;
    let temp = data.values.filter((item) => item.key.startsWith(inputValue.slice(0, Math.min(item?.key?.length, 6))));
    if (!temp.length) return false;
    for (let i = 0; i < temp.length; i++) {
      if (!selectedList[temp[i].key]) {
        return false;
      }
    }
    return true;
  };

  const handleApply = () => {
    closeFilter();
    let tempData = JSON.parse(JSON.stringify(filterList));
    let selectedListData = JSON.parse(JSON.stringify(selectedList));

    // Process tempData to mark checked items
    for (let i = 0; i < tempData.length; i++) {
      if (selectedListData[tempData[i].key]) {
        tempData[i].isChecked = true;
        let isParentSelceted = true;
        if (tempData[i].values && Array.isArray(tempData[i].values)) {
          for (let j = 0; j < tempData[i].values.length; j++) {
            if (selectedListData[tempData[i].values[j].key]) {
              tempData[i].values[j].isChecked = true;
            } else if (isParentSelceted) {
              isParentSelceted = false;
            }
          }
          if (!selectedListData[tempData[i].key] && isParentSelceted) {
            selectedListData[tempData[i].key] = true;
            tempData[i].isChecked = true;
          }
        }
      }
    }

    // Filter out parent codes if their children are selected
    const filteredSelectedKeys = filterParentCodes(Object.keys(selectedListData));
    onApply(includeFilterButton, tempData, filteredSelectedKeys);
  };

  // Helper function to filter out parent codes when children are selected
  const filterParentCodes = (selectedKeys) => {
    const filtered = [];

    selectedKeys.forEach((key) => {
      const keyLength = key.length;
      let hasSelectedChild = false;

      // Check if this key has any selected children
      selectedKeys.forEach((otherKey) => {
        if (otherKey !== key && otherKey.startsWith(key) && otherKey.length > keyLength) {
          hasSelectedChild = true;
        }
      });

      // Only include the key if it doesn't have selected children
      if (!hasSelectedChild) {
        filtered.push(key);
      }
    });

    return filtered;
  };

  // Function to check if there are any matching results
  const hasMatchingResults = () => {
    if (!filterList || filterList.length === 0) return false;
    return filterList.some((data) => data && data.key && data.key.startsWith(inputValue.slice(0, 2))) && inputValue.length <= 6;
  };

  const callingSearchAPIForHSCode = async (key, index) => {
    const extraPayload = {
      hscode_expanded: key,
      target_index: index,
    };
    try {
      await getFilterDataHandler(filterDetails, undefined, inputValue, extraPayload);
    } catch (err) {
      toast.error("Something went wrong!");
    }
  };

  const toggleSubKeyExpansion = (subKey, parentIndex) => {
    if (expandedSubKey === subKey) {
      setExpandedSubKey("");
    } else {
      let filterObjectClone = JSON.parse(JSON.stringify(shipmentDetails.filterObject));
      let cachedHSCodeData = [...filterObjectClone[filterDetails.key]];

      let fourDigitHSCode = subKey;
      let twoDigitIndex = cachedHSCodeData.findIndex((twoDigitHS) => fourDigitHSCode.startsWith(twoDigitHS.key));

      let cached = cachedHSCodeData[twoDigitIndex]?.values[parentIndex]?.values && cachedHSCodeData[twoDigitIndex]?.values[parentIndex]?.values?.length > 0;

      if (!cached) {
        callingSearchAPIForHSCode(subKey, parentIndex);
      }
      setExpandedSubKey(subKey);
    }
  };

  // Helper function to get nested data
  const getNestedData = (parentKey, childKey = null) => {
    let filterObjectClone = JSON.parse(JSON.stringify(shipmentDetails.filterObject));
    let cachedHSCodeData = filterObjectClone[filterDetails.key];

    let parentIndex = Object.keys(cachedHSCodeData).findIndex((key) => cachedHSCodeData[key].key === parentKey);

    if (parentIndex === -1) return null;

    if (!childKey) {
      return cachedHSCodeData[parentIndex]?.values || [];
    }

    let childIndex = cachedHSCodeData[parentIndex].values?.findIndex((item) => item.key === childKey);

    if (childIndex === -1) return null;
    return cachedHSCodeData[parentIndex].values[childIndex]?.values || [];
  };

  // When new children are loaded, if parent is selected, auto-select them
  useEffect(() => {
    if (expandedKey) {
      const children = getNestedData(expandedKey) || [];
      if (selectedList[expandedKey]) {
        let temp = { ...selectedList };
        children.forEach((child) => {
          temp[child.key] = true;
          getAllDescendantKeys(child).forEach((descKey) => {
            temp[descKey] = true;
          });
        });
        setSelectedList(temp);
      }
    }
    if (expandedSubKey && expandedKey) {
      const children = getNestedData(expandedKey, expandedSubKey) || [];
      if (selectedList[expandedSubKey]) {
        let temp = { ...selectedList };
        children.forEach((child) => {
          temp[child.key] = true;
          getAllDescendantKeys(child).forEach((descKey) => {
            temp[descKey] = true;
          });
        });
        setSelectedList(temp);
      }
    }
    // eslint-disable-next-line
  }, [filterList]);

  return (
    <Card className="view-all-filter-panel-card">
      <Card.Header>
        <div className="d-flex align-items-center">
          <div className="col-md-5 p-3">
            <span className="modal_header">{filterDetails?.label || ""} Filters</span>
          </div>
          <div className="col-md-3 pl-0" />
          <div className="col-md-4 text-right pr-0 d-flex justify-content-end align-items-center">
            <button
              type="button"
              disabled={Object.keys(selectedList).length === 0}
              className="btn btn-primary btn-sm trademo-new-btn apply-filter"
              onClick={() => {
                handleApply();
              }}>
              Apply Filter
            </button>
            <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={() => closeFilter()}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="!tw-pt-0">
        <div className={`mb-3 viewall-sticky-header pt-2 tw-pb-4`}>
          <IncludeExcludeFilter includeFilter={includeFilter.includes(filterKeyword)} excludeFilter={excludeFilter.includes(filterKeyword)} setIncludeFilter={(data) => changeIncludeExcludeSelection(data)} />
          <div className="col-md-5 pl-0" id={filterDetails?.label || "" + "filter"}>
            <input
              id="hscode-search-input"
              className="form-control py-1 filter-search ml-3"
              type="text"
              placeholder={`Search in ${filterDetails?.label || ""} filter`}
              value={searchedValue}
              onChange={(e) => {
                setIsLoaderActive(true);
                setSearchedValue(e.target.value);
                setExpandedKey("");
                setExpandedSubKey(""); // Reset sub expansions when searching
              }}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="trademo-hscodeview-accordion ml-1">
          {Object.keys(selectedList).length > 0 && (
            <div className="d-flex flex-wrap mb-2" data-for="appliedExporterCapsule">
              {Object.keys(selectedList).map((item) => (
                <div className="mb-2 mx-2 green-capsule tw-border tw-rounded-3xl" key={item}>
                  <span className="badge filter-no tw-pl-2">HS Code {item}</span>
                  <span className="badge tw-pr-1">
                    <X size={14} color="#5A1CE2" className="cursor-pointer" onClick={() => handleRemove(item)} />
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* {true ? ( */}
          {(isLoaderActive || filterLoader) && expandedKey === "" && expandedSubKey === "" ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "254px" }}>
              <img src={LoaderImg} alt="description of gif" />
            </div>
          ) : hasMatchingResults() ? (
            filterList.map((data, i) =>
              data && inputValue.length < 7 && data.key && data.key.startsWith(inputValue.slice(0, 2)) ? (
                <Card className="collapsed-hscode pt-2 border-b" key={data.key}>
                  {/* 2-digit HS Code Header */}
                  <div
                    className="d-flex justify-content-between align-items-center"
                    onClick={() => {
                      if (expandedKey === data.key) {
                        setExpandedKey(null);
                        setExpandedSubKey(""); // Close sub expansions
                      } else {
                        let filterObjectClone = JSON.parse(JSON.stringify(shipmentDetails.filterObject));
                        let cachedHSCodeData = filterObjectClone[filterDetails.key];
                        let index = Object.keys(cachedHSCodeData).findIndex((key) => cachedHSCodeData[key].key === data.key);
                        if (!cachedHSCodeData[index]?.values) callingSearchAPIForHSCode(data.key, i);
                        setExpandedKey(data.key);
                        setExpandedSubKey(""); // Reset sub expansions
                      }
                    }}>
                    <div className="col-md-auto" onClick={(e) => e.stopPropagation()}>
                      <label className="form-check">
                        <input className="form-check-input" type="checkbox" id={"expFil" + i} checked={checkIfParentSelected(data) || !!selectedList[data.key]} onChange={(e) => handleSelect(e.target.checked, data.values ?? data.key, data.key, data?.values || [], debouncedInputValue)} />
                        <span className="form-check-label">
                          {data.key !== "" ? "HS Code " + data.key.toLowerCase() : "Not Available"}
                          <span className="grey-text">{(data.doc_count === 0 || data.doc_count) && typeof data.doc_count === "number" ? ` (${commaFormat(data.doc_count)})` : data.doc_count ? ` (${data.doc_count})` : ""}</span>
                        </span>
                      </label>
                    </div>
                    <div className="allhscode cursor-pointer">{expandedKey === data.key ? <ChevronUp size={20} color="#191722" /> : <ChevronDown size={20} color="#191722" />}</div>
                  </div>

                  {/* 4-digit HS Code Content */}
                  {expandedKey === data.key && (
                    <div>
                      {(isLoaderActive || filterLoader) && expandedKey !== "" && expandedSubKey === "" ? (
                        <div className="d-flex justify-content-center align-items-center tw-py-6">
                          <LinearDeterminateLoading />
                        </div>
                      ) : getNestedData(data.key)?.filter((item) => item?.key?.startsWith(inputValue.slice(0, 4)))?.length > 0 ? (
                        getNestedData(data.key)
                          ?.filter((item) => item?.key?.startsWith(inputValue.slice(0, 4)))
                          .map((fourDigitItem, index) => (
                            <div key={fourDigitItem.key} className="tw-ml-4">
                              <Card className="collapsed-hscode pt-1 border-b">
                                <div className="tw-flex tw-justify-between tw-items-center">
                                  <Card.Body className="py-0 px-3 tw-max-w-[85%]" onClick={(e) => e.stopPropagation()}>
                                    <label className="form-check pb-2">
                                      <input className="form-check-input" type="checkbox" id={"hscodecollapse" + index + i} checked={checkIfParentSelected(fourDigitItem) || !!selectedList[fourDigitItem.key]} onChange={(e) => handleSelect(e.target.checked, fourDigitItem?.values ?? fourDigitItem.key, fourDigitItem.key, fourDigitItem?.values || [], debouncedInputValue)} />
                                      <span className="form-check-label">
                                        {fourDigitItem?.description !== "" ? `${fourDigitItem?.key} - ${fourDigitItem.description}` : `${fourDigitItem?.key} - Not Available`}
                                        <span className="grey-text">{(fourDigitItem.doc_count === 0 || fourDigitItem.doc_count) && typeof fourDigitItem.doc_count === "number" ? ` (${commaFormat(fourDigitItem.doc_count)})` : fourDigitItem.doc_count ? ` (${fourDigitItem.doc_count})` : ""}</span>
                                      </span>
                                    </label>
                                  </Card.Body>

                                  <div className="tw-cursor-pointer" onClick={() => toggleSubKeyExpansion(fourDigitItem.key, index)}>
                                    {expandedSubKey === fourDigitItem.key ? <ChevronUp size={20} color="#191722" /> : <ChevronDown size={20} color="#191722" />}
                                  </div>
                                </div>
                                {/* 6-digit HS Code Content */}
                                {expandedSubKey === fourDigitItem.key && (
                                  <div className="tw-ml-4 tw-border-l tw-border-gray-200 tw-pl-3">
                                    {(isLoaderActive || filterLoader) && expandedSubKey !== "" && expandedKey !== "" ? (
                                      <div className="d-flex justify-content-center align-items-center tw-py-4">
                                        <LinearDeterminateLoading />
                                      </div>
                                    ) : getNestedData(data.key, fourDigitItem.key)?.filter((item) => item?.key?.startsWith(inputValue.slice(0, 6))).length > 0 ? (
                                      getNestedData(data.key, fourDigitItem.key)
                                        ?.filter((item) => item?.key?.startsWith(inputValue.slice(0, 6)))
                                        .map((sixDigitItem, sixIndex) => (
                                          <div key={sixDigitItem.key} className="tw-py-1">
                                            <Card.Body className="py-1 px-2">
                                              <label className="form-check">
                                                <input className="form-check-input" type="checkbox" id={"hscode6digit" + sixIndex + index + i} checked={!!selectedList[sixDigitItem.key]} onChange={(e) => handleSelect(e.target.checked, sixDigitItem?.key, fourDigitItem.key, fourDigitItem?.values || [], debouncedInputValue)} />
                                                <span className="form-check-label tw-text-sm">
                                                  {sixDigitItem?.description !== "" ? `${sixDigitItem?.key} - ${sixDigitItem.description}` : `${sixDigitItem?.key} - Not Available`}
                                                  <span className="grey-text">{(sixDigitItem.doc_count === 0 || sixDigitItem.doc_count) && typeof sixDigitItem.doc_count === "number" ? ` (${commaFormat(sixDigitItem.doc_count)})` : sixDigitItem.doc_count ? ` (${sixDigitItem.doc_count})` : ""}</span>
                                                </span>
                                              </label>
                                            </Card.Body>
                                          </div>
                                        ))
                                    ) : (
                                      <>
                                        <div className="text-center card-body p-2">
                                          <div className="header-image">
                                            <img className="img-fluid" src={nodataimage} alt="..." />
                                          </div>
                                          <p className="mt-4">No 6 Digit HS Code Found.</p>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </Card>
                            </div>
                          ))
                      ) : (
                        <>
                          <div className="text-center card-body p-2">
                            <div className="header-image">
                              <img className="img-fluid" src={nodataimage} alt="..." />
                            </div>
                            <p className="mt-4">No 4 Digit HS Code Found.</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </Card>
              ) : null
            )
          ) : (
            <ViewAllFilter content={emptyContent} filterKeyword={filterKeyword} label={label} />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default HsCodeViewAll;
